import {
  InterviewPracticeStepCode,
  InterviewPracticeStepCode as StepCode,
} from '@domain/constant/interviewPracticeStep';
import useInterviewPracticeStore from '@stores/InterviewStore/InterviewPracticeStore';
import { ContentRouteUtil } from '@utils/routeUtil';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { ChromeDownload } from './chromeDownload';
import { CommonBackground } from './common/commonBackground';
import { GuideText } from './common/guide';
import { AnyErrorPopup } from './errorPopup/anyErrorPopup';
import { RecordingErrorPopup } from './errorPopup/recordingErrorPopup';
import { Result } from './finish/result';
import { Step1 } from './guide/step1';
import { Step2 } from './guide/step2';
import { Intro } from './intro';
import { Permission } from './permission';
import { RecordTime } from './practice/recordTime';
import { ThinkTime } from './practice/thinkTime';
import { TestEnd } from './test/testEnd';
import { TestProgress } from './test/testProgress';
import { TestStart } from './test/testStart';

const numerals = ['첫', '두', '세', '네', '다섯', '여섯', '일곱', '여덟', '아홉', '열'];

export const InterviewPractice = () => {
  const {
    initialize,
    recorderService,
    anyError,
    playForceTTSAudio,
    pauseTTSAudio,
    allAnswersSubmitted,
    updateSelectedQuestionSnByIndex,
  } = useInterviewPracticeStore((state) => state);
  const practiceQuestionInfo = useInterviewPracticeStore((state) => state.practiceQuestionInfo, shallow);
  const practiceStepInfo = useInterviewPracticeStore((state) => state.practiceStepInfo, shallow);
  const recoderSevervice = useInterviewPracticeStore((state) => state.recorderService, shallow);
  const { currentStep, currentPracticeIndex } = practiceStepInfo;
  const [guideStep1Entered, setGuideStep1Entered] = useState(false);

  const [isChrome, setIsChrome] = useState(true);

  const isBackgroundGray =
    currentStep === InterviewPracticeStepCode.CHROME_DOWNLOAD || currentStep === InterviewPracticeStepCode.PERMISSION;

  const init = async () => {
    await initialize(Number(window.location.href.split('interviewPracticeSn=')[1]));
  };

  useEffect(() => {
    // TODO PR서버에서 if (isReady) return; 했는데도 undefined인 채로 query가 들어가 window.location으로 접근
    const sn = window.location.href.split('interviewPracticeSn=')[1];
    const invalidInterviewPracticeSn = sn === undefined || sn === null || sn === '' || isNaN(Number(sn));
    if (invalidInterviewPracticeSn) {
      ContentRouteUtil.go404();
    } else {
      const flag = navigator.userAgent.toLowerCase().indexOf('chrome') !== -1;
      setIsChrome(flag);
      if (flag) {
        init();
      }
    }
  }, []);

  useEffect(() => {
    if (!practiceQuestionInfo.questionCount) return;
    practiceStepInfo.initialize(practiceQuestionInfo.questionCount, practiceQuestionInfo.currentPracticeIndex, () => {
      return !recoderSevervice.recordingOrWebcamOrMicError && !anyError;
    });
  }, [practiceQuestionInfo]);

  useEffect(() => {
    (async () => {
      switch (currentStep) {
        case StepCode.INTRO:
          await playForceTTSAudio('안녕하세요. 지금부터 저와 함께 면접 연습을 진행해 볼게요.');
          break;
        case StepCode.TEST_START:
          await playForceTTSAudio('지원자님의 환경상태를 체크하세요.');
          break;
        case StepCode.TEST_END:
          await playForceTTSAudio('환경상태 체크가 완료되었습니다.');
          break;
        case StepCode.PRACTICE_GUIDE:
          await playForceTTSAudio(
            `${numerals[currentPracticeIndex]} 번째 질문입니다.카메라를 보고 편안하게 이야기해주세요!`,
          );
          break;
        case StepCode.PRACTICE_SUBMITTED_GUIDE:
          await playForceTTSAudio(
            `답변이 잘 제출되었습니다. 이제 ${numerals[currentPracticeIndex]} 번째 질문을 드리겠습니다.`,
          );
          break;
        case StepCode.FINISH_GUIDE:
          await playForceTTSAudio('모든 질문이 끝났어요! 응답을 확인하고 있습니다.');
          break;
        default:
          /*
                        다음 이동시 TTS 재생을 강제 중지해야하기 때문입니다.
                        playForceTTSAudio 코드에는 pauseTTSAudio가 삽입되어 있습니다.
                     */
          pauseTTSAudio();
      }
    })();
  }, [currentStep]);

  const loadResultData = () => {
    if (allAnswersSubmitted) {
      updateSelectedQuestionSnByIndex(0);
    } else {
      /*
                단순히 모든 동영상이 저장되기 전까지 다음 데이터를 조회하지 않기 위한 목적입니다. 일종의 콜백을 위한 코드입니다.
                추후 requestAnimationFrame을 사용하지 않고도, request를 대기할 수 있도록 (lock 기능 등) 개선 부탁드립니다.
             */
      requestAnimationFrame(() => loadResultData());
    }
  };

  const getComponent = () => {
    switch (currentStep) {
      case StepCode.PERMISSION:
        return <Permission />;
      case StepCode.INTRO:
        return <Intro />;
      case StepCode.TEST_START:
        return <TestStart />;
      case StepCode.TEST_PROGRESS:
        return <TestProgress />;
      case StepCode.TEST_END:
        return <TestEnd />;
      case StepCode.GUIDE_STEP1:
        return <Step1 entered={guideStep1Entered} />;
      case StepCode.GUIDE_STEP2:
        return <Step2 />;
      case StepCode.PRACTICE_GUIDE:
        return (
          <GuideText>
            {numerals[currentPracticeIndex]} 번째 질문입니다.
            <br />
            카메라를 보고 편안하게 이야기해주세요!
          </GuideText>
        );
      case StepCode.PRACTICE_THINK:
        return <ThinkTime />;
      case StepCode.PRACTICE_SUBMITTED_GUIDE:
        return (
          <GuideText>
            답변이 잘 제출되었습니다 <br />
            이제 {numerals[currentPracticeIndex]} 번째 질문을 드리겠습니다.
          </GuideText>
        );
      case StepCode.PRACTICE_RECORD:
        return <RecordTime />;
      case StepCode.FINISH_GUIDE:
        return (
          <GuideText onMount={loadResultData}>
            모든 질문이 끝났어요!
            <br />
            응답을 확인하고 있습니다.
          </GuideText>
        );
      case StepCode.FINISH_RESULT:
        return <Result />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (currentStep === InterviewPracticeStepCode.GUIDE_STEP1 && !guideStep1Entered) {
      setGuideStep1Entered(true);
    }
  }, [currentStep]);

  if (recorderService.recordingOrWebcamOrMicError) {
    return (
      <CommonBackground>
        <RecordingErrorPopup />
      </CommonBackground>
    );
  }
  if (anyError) {
    return (
      <CommonBackground>
        <AnyErrorPopup />
      </CommonBackground>
    );
  }
  return (
    <>
      <CommonBackground isGray={isBackgroundGray} isGreen={currentStep === StepCode.INTRO}>
        {!isChrome && <ChromeDownload />}
        {isChrome && getComponent()}
      </CommonBackground>
    </>
  );
};
